import React, { forwardRef, useState, useEffect } from 'react';
import axios from '../../../../../services/AxiosInstance';
import { format } from 'date-fns';
import { getApiUrl } from '../../../../../utils/ApiUtils';

const QuotePreviewBootstrap = forwardRef(({
  templateId,
  customer,
  company,
  invoiceNumber,
  invoiceDate,
  dueDate,
  items,
  notes,
  paymentInfo,
  paymentMethods = { bankTransfer: true, creditCard: false, cash: false },
  roundTotal = false,
  footerData: customFooterData
}, ref) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState(null);

  // Prepare invoice data for backend
  useEffect(() => {
    // Reset state when dependencies change
    setPdfUrl(null);
    setError(null);

    // Don't fetch if we don't have minimum required data
    if (!customer || !items.length) {
      return;
    }

    // Generate a request ID to help prevent race conditions
    const currentRequestId = Date.now().toString();
    setRequestId(currentRequestId);
    setIsLoading(true);

    // Prepare data for API
    const invoiceData = {
      templateId,
      customer,
      company,
      invoiceNumber,
      invoiceDate: format(invoiceDate, 'yyyy-MM-dd'),
      dueDate: format(dueDate, 'yyyy-MM-dd'),
      items,
      notes,
      paymentInfo,
      paymentMethods,
      roundTotal,
      footerData: customFooterData
    };

    // API call to generate PDF
    const fetchPdf = async () => {
      try {
        const response = await axios.post(
          getApiUrl('/api/invoices/preview-pdf'),
          invoiceData,
          { responseType: 'blob' }
        );

        // Only update state if this is still the current request
        if (requestId === currentRequestId) {
          // Create blob URL from response
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          setPdfUrl(url);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching PDF:', err);

        // Only update error state if this is still the current request
        if (requestId === currentRequestId) {
          setError('Det gick inte att generera PDF:en. Försök igen senare.');
          setIsLoading(false);
        }
      }
    };

    // Set a small delay to prevent too many requests during rapid changes
    const timer = setTimeout(() => {
      fetchPdf();
    }, 500);

    return () => {
      clearTimeout(timer);
      // Cleanup blob URL on unmount
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [
    templateId,
    customer?.id,
    company?.id,
    invoiceNumber,
    invoiceDate,
    dueDate,
    // We stringify these complex objects to properly detect changes
    JSON.stringify(items),
    notes,
    JSON.stringify(paymentMethods),
    roundTotal,
    JSON.stringify(customFooterData)
  ]);

  // If there's no customer or items, show placeholder
  if (!customer || items.length === 0) {
    return (
      <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded">
        <div className="text-center p-4">
          <h5 className="text-secondary mb-3">Faktura förhandsgranskning</h5>
          <p className="text-muted">
            Välj en kund och lägg till artiklar för att se förhandsgranskningen.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Laddar...</span>
          </div>
          <p className="text-muted">Genererar faktura förhandsgranskning...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded">
        <div className="text-center text-danger p-4">
          <div className="mb-3">
            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '2rem' }}></i>
          </div>
          <h5 className="mb-3">Det gick inte att generera förhandsgranskningen</h5>
          <p className="mb-4">{error}</p>
          <button
            className="btn btn-outline-primary"
            onClick={() => {
              setError(null);
              setRequestId(Date.now().toString());
              setIsLoading(true);
            }}
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  // Render PDF if we have a URL
  if (pdfUrl) {
    return (
      <div ref={ref} className="overflow-auto bg-light h-100 rounded">
        <div className="d-flex flex-column h-100">
          <object
            data={pdfUrl}
            type="application/pdf"
            className="w-100 h-100 rounded"
          >
            <div className="d-flex align-items-center justify-content-center h-100 bg-light p-4">
              <div className="text-center">
                <p className="mb-3">PDF-visaren stöds inte i din webbläsare.</p>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Öppna PDF i ny flik
                </a>
              </div>
            </div>
          </object>
        </div>
      </div>
    );
  }

  // Fallback state - waiting for PDF to be generated but not showing loader yet
  return (
    <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded">
      <div className="text-center p-4">
        <p className="text-muted">Förbereder PDF-förhandsgranskning...</p>
      </div>
    </div>
  );
});

QuotePreviewBootstrap.displayName = 'QuotePreviewBootstrap';

export default QuotePreviewBootstrap;



// import React, { forwardRef } from 'react';
// import { invoiceTemplatesJs } from './invoiceTemplatesJs.jsx';
// import { format } from 'date-fns';

// const InvoicePreviewBootstrap = forwardRef(({
//   templateId,
//   customer,
//   company,
//   invoiceNumber,
//   invoiceDate,
//   dueDate,
//   items,
//   notes,
//   paymentInfo,
//   paymentMethods = { bankTransfer: true, creditCard: false, cash: false },
//   roundTotal = false,
//   footerData: customFooterData
// }, ref) => {
//   const selectedTemplate = invoiceTemplatesJs.find(t => t.id === templateId);

//   // Transform customer data to fit template expectations
//   const prepareCustomerData = (rawCustomer) => {
//     if (!rawCustomer) return null;

//     // Get the primary billing address
//     let billingAddress = null;
//     if (rawCustomer.addresses && rawCustomer.addresses.length > 0) {
//       billingAddress = rawCustomer.addresses.find(a => a.address_type === 'billing') || rawCustomer.addresses[0];
//     }

//     // Get primary contact
//     let primaryContact = null;
//     if (rawCustomer.contacts && rawCustomer.contacts.length > 0) {
//       primaryContact = rawCustomer.contacts[0];
//     }

//     // Return transformed customer object that matches template expectations
//     return {
//       ...rawCustomer,
//       contactName: primaryContact?.contact_name || '',
//       address: {
//         street: billingAddress?.street1 || '',
//         street2: billingAddress?.street2 || '',
//         city: billingAddress?.city || '',
//         state: '', // May need mapping
//         zipCode: billingAddress?.postalcode || '',
//         country: billingAddress?.country || ''
//       }
//     };
//   };

//   // Transform company data to ensure it has the structure templates expect
//   const prepareCompanyData = (rawCompany) => {
//     if (!rawCompany) return null;

//     return {
//       ...rawCompany,
//       address: {
//         street: rawCompany.address?.street || '',
//         city: rawCompany.address?.city || '',
//         state: rawCompany.address?.state || '',
//         zipCode: rawCompany.address?.zipCode || ''
//       },
//       registrationNumber: rawCompany.registrationNumber || rawCompany.orgnumber || 'N/A',
//       vatNumber: rawCompany.vatNumber || rawCompany.vatnumber || 'N/A'
//     };
//   };

//   // Prepare the customer and company data
//   const formattedCustomer = prepareCustomerData(customer);
//   const formattedCompany = prepareCompanyData(company);

//   if (!selectedTemplate || !formattedCustomer) {
//     return (
//       <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded border border-dashed">
//         <div className="text-center">
//           <h5 className="text-secondary">Invoice Preview</h5>
//           <p className="text-muted small">
//             {!selectedTemplate ? 'Select a template' : 'Select a customer and add items'}
//           </p>
//         </div>
//       </div>
//     );
//   }

//   // Calculate subtotal
//   const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

//   // Group items by tax rate for detailed tax breakdown
//   const taxGroups = {};
//   items.forEach(item => {
//     const itemTax = item.tax || 0;
//     if (!taxGroups[itemTax]) {
//       taxGroups[itemTax] = 0;
//     }
//     taxGroups[itemTax] += (item.price * item.quantity);
//   });

//   // Calculate tax amounts for each tax group
//   const taxDetails = Object.entries(taxGroups).map(([rate, amount]) => ({
//     rate: parseFloat(rate),
//     amount: (parseFloat(rate) / 100) * amount,
//     subtotal: amount
//   }));

//   // Calculate total tax
//   const taxAmount = taxDetails.reduce((sum, tax) => sum + tax.amount, 0);

//   // Calculate total before rounding
//   const totalBeforeRounding = subtotal + taxAmount;

//   // Apply rounding if enabled
//   let roundingAdjustment = 0;
//   let total = totalBeforeRounding;

//   if (roundTotal && total > 0) {
//     const roundedTotal = Math.round(total);
//     roundingAdjustment = roundedTotal - total;
//     total = roundedTotal;
//   }

//   // Create payment methods text
//   const paymentMethodsText = Object.entries(paymentMethods)
//     .filter(([_, enabled]) => enabled)
//     .map(([method]) => {
//       switch (method) {
//         case 'bankTransfer': return 'Bankgiro';
//         case 'creditCard': return 'Kreditkort';
//         case 'cash': return 'Kontant';
//         case 'swish': return 'Swish';
//         case 'paypal': return 'PayPal';
//         default: return '';
//       }
//     })
//     .join('\n');

//   // Create the footer data structure or use custom footer if provided
//   const footerData = customFooterData || {
//     col1: `${formattedCompany.name}\n${formattedCompany.address.street}\n${formattedCompany.address.city}, ${formattedCompany.address.state} ${formattedCompany.address.zipCode}`,
//     col2: `Organisationsnummer: ${formattedCompany.registrationNumber}\nMomsreg.nr: ${formattedCompany.vatNumber}\nGodkänd för F-skatt: ${formattedCompany.taxId || 'Ja'}`,
//     col3: paymentMethodsText ? `Betalningsmetoder:\n${paymentMethodsText}` : 'Betalningsmetoder:\nBankgiro'
//   };

//   const templateProps = {
//     customer: formattedCustomer,
//     company: formattedCompany,
//     invoiceNumber,
//     invoiceDate: format(invoiceDate, 'yyyy-MM-dd'), // Swedish date format
//     dueDate: format(dueDate, 'yyyy-MM-dd'),
//     items,
//     notes,
//     subtotal,
//     taxDetails,
//     taxAmount,
//     total,
//     roundingAdjustment,
//     className: 'invoice-preview shadow-sm rounded',
//     footerData,
//     roundTotal
//   };

//   return (
//     <div ref={ref} className="overflow-auto bg-light h-100 rounded">
//       <div className="min-width-700 p-3">
//         <div className="a4-page-container">
//           {selectedTemplate.render(templateProps)}
//         </div>
//       </div>
//     </div>
//   );
// });

// InvoicePreviewBootstrap.displayName = 'InvoicePreviewBootstrap';

// export default InvoicePreviewBootstrap;